import { Search, CustomSelect, SelectOption } from '@consigli/facade';
import {
  useDebouncedSearch,
  useGetBlobUniqueDocumentTypesQuery,
  usePackageId,
  useProjectId,
} from '@consigli/hooks';
import { FoldersDocumentType } from '@consigli/types';
import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useFileTreeContext } from '@/pages/insight/folders/use-file-tree-context';

import { status } from './draggable-container';

export const DocumentFilters: FC = () => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const packageId = usePackageId();

  const {
    activeFolderId,
    selectedDocumentTypes,
    setSelectedDocumentTypes,
    searchContent,
    setSearchContent,
    search,
    setSearch,
    setPage,
    labelsReused,
  } = useFileTreeContext();
  const debouncedSearchContent = useDebouncedSearch(searchContent, 500);
  const debouncedSearch = useDebouncedSearch(search, 500);

  const { data: documentTypes } = useGetBlobUniqueDocumentTypesQuery({
    projectId,
    packageId,
    searchParsedData: debouncedSearchContent.length >= 2 ? debouncedSearchContent : '',
    standard: activeFolderId,
    documentType: selectedDocumentTypes.map((option) => option.value as FoldersDocumentType),
    search: debouncedSearch,
    status,
    labelsReused,
  });

  const documentTypeOptions =
    documentTypes?.map((documentType) => ({
      label: t('folders.document-type.' + documentType),
      value: documentType,
    })) || [];

  const handleDocumentTypeSelect = useCallback(
    (options: SelectOption[]): void => {
      if (options.length > 0) {
        setSelectedDocumentTypes(options);
      } else {
        setSelectedDocumentTypes([]);
      }
      setPage(1);
    },
    [setSelectedDocumentTypes, setPage],
  );

  const handleSearchContent = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchContent(event.target.value);
      setPage(1);
    },
    [setSearchContent, setPage],
  );
  const handleSearchName = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(event.target.value);
      setPage(1);
    },
    [setSearch, setPage],
  );

  return (
    <div className="flex flex-1 gap-x-2">
      <div className="flex-1">
        <CustomSelect
          options={documentTypeOptions}
          onChange={handleDocumentTypeSelect}
          value={selectedDocumentTypes}
          placeholder={t('folders.filter-on-document-type')}
          noOptionsMessage={t('custom-select.no-options')}
          isClearable
          isMulti
        />
      </div>
      <div className="flex-1">
        <Search
          text={search}
          onChange={handleSearchName}
          placeholder={t('findings.search-document-names')}
          className="w-full bg-white rounded"
        />
      </div>
      <div className="flex-1">
        <Search
          text={searchContent}
          onChange={handleSearchContent}
          placeholder={t('findings.search_content')}
          className="w-full bg-white rounded"
        />
      </div>
    </div>
  );
};
